import React from 'react';
import { isNil } from 'lodash';

import colors from '~/assets/colors.module.scss';
import { useStyles } from '~/assets/styles';
import Typography from '~/components/core/Atomic/Typography';
import TooltipIcon from '~/components/core/TooltipIcon';
import { PencilIcon, WarningIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import type {
  AvailableRecoveryProviderInfoList,
  RecoveryRequestsConfigInfo,
  RecoveryRequestsConfigInfoList,
} from '~/server_shared/openapi/data-contracts';

import styles from '../PaymentsConfigurationForm.module.scss';

/**
 * The row one scope has of its own — the sub-org's own row, or the organization-level row when no
 * sub-org is named. A scope with no row of its own is unconfigured, not defaulted in: that is the
 * distinction the dialog needs to tell an inherited value from a set one.
 *
 * The org-level row is identified by a null `sub_organization_id`, so the test is nullability
 * (`isNil`) rather than truthiness — a falsy check would also claim a sub-org whose id is 0.
 */
export const findOwnRecoveryRequestsConfig = (
  configs: RecoveryRequestsConfigInfoList,
  subOrgId?: number
): RecoveryRequestsConfigInfo | undefined =>
  subOrgId === undefined
    ? configs.find((config) => isNil(config.sub_organization_id))
    : configs.find((config) => config.sub_organization_id === subOrgId);

/**
 * Resolve the row that governs one scope, the way the backend's get_config does: the sub-org row
 * wins, the org-level row is the fallback, and neither means nothing is configured. A cell that
 * resolved differently would disagree with the configuration a request actually runs under.
 */
export const resolveRecoveryRequestsConfig = (
  configs: RecoveryRequestsConfigInfoList,
  subOrgId?: number
): RecoveryRequestsConfigInfo | undefined =>
  findOwnRecoveryRequestsConfig(configs, subOrgId) ?? findOwnRecoveryRequestsConfig(configs, undefined);

/**
 * The values the configured handler needs from this scope and has not been given.
 *
 * The declarations come from the registry listing, so no handler is named here and one landing with
 * a different set of fields needs no change. Only an *enabled* scope is reported on: a scope that is
 * off sends nothing, so nothing is missing from it yet.
 *
 * A blank string counts as missing, matching the backend's own required-field check — a row written
 * with an empty template code fails a send exactly as an absent one does.
 */
const missingRequiredFieldLabels = (
  config: RecoveryRequestsConfigInfo | undefined,
  availableProviders: AvailableRecoveryProviderInfoList
): string[] => {
  if (!config?.is_enabled) {
    return [];
  }
  const provider = availableProviders.find((candidate) => candidate.provider_key === config.provider_key);
  return (provider?.configuration_fields ?? [])
    .filter((field) => field.is_required && !(config.provider_config?.[field.key] ?? '').trim())
    .map((field) => field.label);
};

export interface RecoveryRequestSpecialCellProps {
  subOrgId?: number;
  configs: RecoveryRequestsConfigInfoList;
  availableProviders: AvailableRecoveryProviderInfoList;
  onEdit: (subOrganizationId: number | null) => void;
  isDisabled?: boolean;
}

const RecoveryRequestSpecialCell: React.FC<RecoveryRequestSpecialCellProps> = ({
  subOrgId,
  configs,
  availableProviders,
  onEdit,
  isDisabled,
}) => {
  const { classes } = useStyles();
  const config = resolveRecoveryRequestsConfig(configs, subOrgId);
  const providerLabel = availableProviders.find(
    (provider) => provider.provider_key === config?.provider_key
  )?.display_name;
  const missingFieldLabels = missingRequiredFieldLabels(config, availableProviders);

  return (
    <div className={styles.methodEnablementStatusContainer}>
      {config?.is_enabled ? (
        // Fall back to the stored key when no registered provider matches it: a row written before a
        // provider module was removed from a deployment must still be legible, not blank.
        <Typography className={styles.methodEnabledText}>{providerLabel ?? config.provider_key}</Typography>
      ) : (
        <Typography className={styles.methodDisabledText}>Not enabled</Typography>
      )}
      {missingFieldLabels.length > 0 ? (
        // The scope reads as configured and every send from it fails, so the gap has to be visible
        // here rather than only once an adjuster hits it. The tooltip names the fields so the
        // pencil beside it is an instruction, not a hunt.
        <TooltipIcon title={`Configuration incomplete: ${missingFieldLabels.join(', ')}`}>
          <WarningIcon iconColor={colors.statusWarning} />
        </TooltipIcon>
      ) : null}
      <InlineIconButton
        className={classes.hoverableNonFilledIcon}
        onClick={() => onEdit(subOrgId ?? null)}
        disabled={isDisabled}
        icon={PencilIcon}
        tooltipTitle="Configure Recovery Requests"
      />
    </div>
  );
};

export default RecoveryRequestSpecialCell;
